import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useFilterSamplingProcedure from "@app/hooks/useFilterSamplingProcedure";
import { TYPE_ANALYSIS_ALFATOTAL } from "@app/pages/samples/utils/constants";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { convertToNumber } from "@app/utils/functions";
import {
  useEquipment,
  useTypesUnits,
  useUser,
} from "@services/servicesGenericsTables";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

const PreparationAlfaEvaporation = ({
  type,
}: {
  type: TYPE_ANALYSIS_ALFATOTAL;
}) => {
  const form = useFormContext();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const filteredProcedures = useFilterSamplingProcedure("ATPRE");
  const { typesUnitsis } = useTypesUnits();
  const watchTypePreparation = form.watch("typePreparation");
  const [validPreparation, setValidPreparation] = useState<string | undefined>(
    undefined
  );

  const { currentPreparation } = useAnalysis();

  useEffect(() => {
    form.setValue(
      "mg_torta_start_limit",
      type === TYPE_ANALYSIS_ALFATOTAL.EVAPORATION ? "50" : "12"
    );
  }, [watchTypePreparation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const mgTorta = form.watch("mg_torta");
    if (!mgTorta) return setValidPreparation(undefined);
    const limit = parseFloat(form.watch("mg_torta_start_limit")) || 0;
    const limitend = parseFloat(form.watch("mg_torta_end_limit")) || 0;

    if (type === TYPE_ANALYSIS_ALFATOTAL.EVAPORATION) {
      if (mgTorta <= limit) {
        form.setValue("valid_preparation", "S");
        setValidPreparation("S");
      } else {
        form.setValue("valid_preparation", "N");
        setValidPreparation("N");
      }
    } else if (type === TYPE_ANALYSIS_ALFATOTAL.COPRECIPITATION) {
      if (mgTorta >= limit && mgTorta <= limitend) {
        form.setValue("valid_preparation", "S");
        setValidPreparation("S");
      } else {
        form.setValue("valid_preparation", "N");
        setValidPreparation("N");
      }
    }
  }, [form.watch("mg_torta"), type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const planchette = parseFloat(form.watch("planchette")) || 0;
    const planchetteWithPrecipitate =
      convertToNumber(form.watch("planchette_with_precipitate")) || 0;

    const calculatedMgTorta = (planchetteWithPrecipitate - planchette) * 1000;

    if (!isNaN(calculatedMgTorta)) {
      form.setValue("mg_torta", calculatedMgTorta.toFixed(4));
    }
  }, [form.watch("planchette"), form.watch("planchette_with_precipitate")]); // eslint-disable-line react-hooks/exhaustive-deps

  const stylesInput = useMemo(() => {
    if (validPreparation === "N") {
      return { backgroundColor: "#ef233c" };
    } else if (validPreparation === "S") {
      return { backgroundColor: "#a7c957" };
    }
    return { backgroundColor: "#fff" };
  }, [validPreparation]);

  return (
    <ResponsiveFormContainer columns={5}>
      <MAutocomplete
        key={JSON.stringify(form.getValues("analysis_analyst"))}
        name="analysis_analyst"
        options={userList}
        label="Seleccione un analista"
        getOptionLabel={(option) => option.username || ""}
        validation={{ required: "El analista es requerido" }}
        disabled={!currentPreparation.canEdit}
      />

      <MAutocomplete
        key={JSON.stringify(form.getValues("preparation_procedure"))}
        name="preparation_procedure"
        options={filteredProcedures}
        label="Procedimiento MLPMA"
        getOptionLabel={(option) => `${option.numop}` || ""}
        validation={{ required: "El procedimiento es requerido" }}
        disabled
      />
      <MDatePicker
        name="start_date"
        pickerType="date"
        label="Fecha de inicio"
        validation={{
          required: "La fecha es requerida",
        }}
        disabled={!currentPreparation.canEdit}
      />
      <MAutocomplete
        key={JSON.stringify(form.getValues("analysis_equipment"))}
        name="analysis_equipment"
        options={equipmentList}
        label="Seleccione un Equipo"
        getOptionLabel={(option) =>
          `${option.codigo} - ${option.descripcion}` || ""
        }
        multiple={true}
        disabled={!currentPreparation.canEdit}
      />
      <MTextField
        key={JSON.stringify(form.getValues("analysis_amount_sample"))}
        name="analysis_amount_sample"
        placeholder="Cantidad de muestra"
        label="Cantidad de muestra"
        type="number"
        validation={{ required: "La cantidad de muestra es requerida" }}
        fullWidth
        disabled={!currentPreparation.canEdit}
      />

      <MTextField
        name="planchette"
        placeholder={
          type === TYPE_ANALYSIS_ALFATOTAL.EVAPORATION
            ? "Plancheta (g)"
            : "Plancheta y Filtro vacío (g)"
        }
        label={
          type === TYPE_ANALYSIS_ALFATOTAL.EVAPORATION
            ? "Plancheta"
            : "Plancheta y Filtro vacío"
        }
        type="number"
        fullWidth
        disabled={!currentPreparation.canEdit}
      />
      <MTextField
        name="planchette_with_precipitate"
        placeholder={
          type === TYPE_ANALYSIS_ALFATOTAL.EVAPORATION
            ? "Plancheta con precipitado"
            : "Plancheta y Filtro con precipitado"
        }
        label={
          type === TYPE_ANALYSIS_ALFATOTAL.EVAPORATION
            ? "Plancheta con precipitado"
            : "Plancheta y Filtro con precipitado"
        }
        fullWidth
        type="number"
        step="0.0001"
        disabled={!currentPreparation.canEdit}
      />
      <MTextField
        name="mg_torta"
        placeholder="mg de torta"
        label="mg de torta"
        type="number"
        fullWidth
        disabled
      />
      <MAutocomplete
        key={JSON.stringify(form.getValues("analysis_unit_types"))}
        name="analysis_unit_types"
        options={typesUnitsis}
        label="Seleccione un tipo de unidad"
        getOptionLabel={(option) => `${option.codigo} | ${option.descripcion}`}
      />
      {(type === TYPE_ANALYSIS_ALFATOTAL.COPRECIPITATION ||
        type === TYPE_ANALYSIS_ALFATOTAL.EVAPORATION) && (
        <MTextField
          name="mg_torta_start_limit"
          key={JSON.stringify(form.watch("mg_torta_start_limit"))}
          placeholder={"mg de torta ≥"}
          label={`mg de torta ≥ ${form.watch("mg_torta_start_limit")}`}
          type="number"
          validation={{ required: "El límite de mg de torta es requerido" }}
          fullWidth
          step="0.0001"
        />
      )}
      {type === TYPE_ANALYSIS_ALFATOTAL.COPRECIPITATION && (
        <MTextField
          name="mg_torta_end_limit"
          placeholder={"mg de torta ≤"}
          label={`mg de torta ≤ ${form.watch("mg_torta_end_limit")}`}
          type="number"
          validation={{ required: "El límite de mg de torta es requerido" }}
          fullWidth
          step="0.0001"
        />
      )}
      {(type === TYPE_ANALYSIS_ALFATOTAL.COPRECIPITATION ||
        type === TYPE_ANALYSIS_ALFATOTAL.EVAPORATION) && (
        <MTextField
          key={JSON.stringify(form.getValues("valid_preparation"))}
          name="valid_preparation"
          placeholder="Preparación Válida"
          label="Preparación Válida"
          value={validPreparation}
          fullWidth
          disabled
          style={stylesInput}
        />
      )}
    </ResponsiveFormContainer>
  );
};

export default PreparationAlfaEvaporation;
